import Swiper from "swiper";
import { Navigation } from "swiper/modules";

interface MediaSlide {
  type: "image" | "video";
  mediaId: string;
  src: string;
  fullSize: string;
  alt: string;
  aspectRatio: number;
  videoHtml: string;
}

const ZOOM_SCALE = 2;
const CLOSE_TRANSITION_MS = 300;
const ALLOWED_MEDIA_TYPES = new Set(["image", "video"]);

class ProductLightbox extends HTMLElement {
  private overlay: HTMLElement | null = null;
  private swiper: Swiper | null = null;
  private isZoomed = false;
  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;
  private boundGalleryClick = this.handleGalleryClick.bind(this);
  private boundVariantUpdate = this.handleVariantUpdate.bind(this);
  private galleryListenerTimeoutId: number | null = null;

  connectedCallback() {
    this.galleryListenerTimeoutId = window.setTimeout(() => {
      this.galleryListenerTimeoutId = null;
      this.gallery?.addEventListener("click", this.boundGalleryClick);
    }, 0);
    document.addEventListener("cc-variant-updated", this.boundVariantUpdate);
  }

  disconnectedCallback() {
    if (this.galleryListenerTimeoutId !== null) {
      clearTimeout(this.galleryListenerTimeoutId);
      this.galleryListenerTimeoutId = null;
    }
    this.close();
    this.gallery?.removeEventListener("click", this.boundGalleryClick);
    document.removeEventListener("cc-variant-updated", this.boundVariantUpdate);
  }

  private get gallery(): HTMLElement | null {
    return (
      this.closest(".theme-gallery") || document.querySelector(".theme-gallery")
    );
  }

  private handleGalleryClick(e: Event) {
    const target = e.target as HTMLElement;
    const themeImg = target.closest<HTMLElement>(".theme-img");
    if (!themeImg) return;

    const productMedia = themeImg.querySelector<HTMLElement>(".product-media");
    if (!productMedia) return;

    const mediaType = productMedia.dataset.mediaType;
    if (!mediaType || !ALLOWED_MEDIA_TYPES.has(mediaType)) return;

    if (this.isSwiperDragging(themeImg)) return;

    e.preventDefault();
    e.stopPropagation();

    const slides = this.collectSlides();
    if (slides.length === 0) return;

    const clickedMediaId = productMedia.dataset.mediaId || "";
    const startIndex = Math.max(
      0,
      slides.findIndex((s) => s.mediaId === clickedMediaId),
    );

    this.open(slides, startIndex);
  }

  private handleVariantUpdate() {
    if (!this.overlay || !this.swiper) return;

    const slides = this.collectSlides();
    if (slides.length === 0) {
      this.close();
      return;
    }

    this.swiper.removeAllSlides();
    slides.forEach((slide) =>
      this.swiper!.appendSlide(this.createSlideElement(slide)),
    );
    this.swiper.slideTo(0, 0);
  }

  private handleImageClick(e: MouseEvent, container: HTMLElement) {
    const img = container.querySelector<HTMLImageElement>(
      ".product-lightbox__image",
    );
    if (!img) return;

    if (this.isZoomed) {
      this.zoomOut(img, container);
    } else {
      this.zoomIn(e, img, container);
    }
  }

  private zoomIn(e: MouseEvent, img: HTMLImageElement, container: HTMLElement) {
    this.isZoomed = true;

    const rect = container.getBoundingClientRect();
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    const rectLeft = rect.left;
    const rectTop = rect.top;

    // Clamp bounds based on how far the scaled image extends beyond the container
    const maxX = Math.max(0, (img.offsetWidth * ZOOM_SCALE - rect.width) / 2);
    const maxY = Math.max(0, (img.offsetHeight * ZOOM_SCALE - rect.height) / 2);

    const clamp = (val: number, min: number, max: number) =>
      Math.max(min, Math.min(max, val));

    // Initial offset based on tap/click position, clamped to edges
    let currentX = clamp(halfW - (e.clientX - rectLeft), -maxX, maxX);
    let currentY = clamp(halfH - (e.clientY - rectTop), -maxY, maxY);

    img.style.willChange = "transform";
    img.style.transform = `translate(${currentX}px, ${currentY}px) scale(${ZOOM_SCALE})`;
    container.classList.add("product-lightbox__image-container--zoomed");
    this.overlay?.classList.add("product-lightbox__overlay--zoomed");

    if (this.swiper) {
      this.swiper.allowTouchMove = false;
    }

    let rafId = 0;

    // Desktop: position-based tracking (unchanged)
    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!this.isZoomed) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const x = halfW - (moveEvent.clientX - rectLeft);
        const y = halfH - (moveEvent.clientY - rectTop);
        img.style.transform = `translate(${x}px, ${y}px) scale(${ZOOM_SCALE})`;
      });
    };

    // Mobile: drag-delta panning — disable CSS transition for instant response
    let lastTouchX = 0;
    let lastTouchY = 0;
    let isDragging = false;

    const applyTransform = () => {
      img.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) scale(${ZOOM_SCALE})`;
    };

    const onTouchStart = (touchEvent: TouchEvent) => {
      const touch = touchEvent.touches[0];
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;
      isDragging = true;

      // Kill CSS transition so dragging feels instant
      img.style.transition = "none";
    };

    const onTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      if (!this.isZoomed || !isDragging) return;

      const touch = moveEvent.touches[0];
      const deltaX = touch.clientX - lastTouchX;
      const deltaY = touch.clientY - lastTouchY;
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;

      currentX = clamp(currentX + deltaX, -maxX, maxX);
      currentY = clamp(currentY + deltaY, -maxY, maxY);

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        applyTransform();
      });
    };

    const onTouchEnd = () => {
      isDragging = false;
      // Restore CSS transition for zoom-out animation
      img.style.transition = "";
    };

    container.addEventListener("mousemove", onMouseMove);
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });
    (container as any).__lightboxCleanup = () => {
      if (rafId) cancelAnimationFrame(rafId);
      container.removeEventListener("mousemove", onMouseMove);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      img.style.willChange = "";
      img.style.transition = "";
    };
  }

  private zoomOut(img: HTMLImageElement, container: HTMLElement) {
    this.isZoomed = false;

    img.style.transform = "";
    container.classList.remove("product-lightbox__image-container--zoomed");
    this.overlay?.classList.remove("product-lightbox__overlay--zoomed");

    if (this.swiper) {
      this.swiper.allowTouchMove = true;
    }
    this.cleanupLightboxListener(container);
  }

  private resetZoom() {
    if (!this.overlay) return;
    this.isZoomed = false;

    this.overlay
      .querySelectorAll<HTMLElement>(".product-lightbox__image-container")
      .forEach((container) => {
        const img = container.querySelector<HTMLImageElement>(
          ".product-lightbox__image",
        );
        if (img) img.style.transform = "";
        container.classList.remove("product-lightbox__image-container--zoomed");
        this.cleanupLightboxListener(container);
      });

    this.overlay.classList.remove("product-lightbox__overlay--zoomed");
    if (this.swiper) this.swiper.allowTouchMove = true;
  }

  private cleanupLightboxListener(container: HTMLElement) {
    const cleanup = (container as any).__lightboxCleanup;
    if (cleanup) {
      cleanup();
      delete (container as any).__lightboxCleanup;
    }
  }

  private collectSlides(): MediaSlide[] {
    const gallery = this.gallery;
    if (!gallery) return [];

    const slides: { slide: MediaSlide; originalIndex: number }[] = [];

    gallery
      .querySelectorAll<HTMLElement>(".theme-img")
      .forEach((themeImg, domIndex) => {
        if (themeImg.getAttribute("aria-hidden") === "true") return;
        if (themeImg.style.display === "none") return;

        const productMedia =
          themeImg.querySelector<HTMLElement>(".product-media");
        if (!productMedia) return;

        const mediaType = productMedia.dataset.mediaType || "image";
        if (!ALLOWED_MEDIA_TYPES.has(mediaType)) return;

        const mediaId = productMedia.dataset.mediaId || "";
        const originalIndex = parseInt(
          themeImg.dataset.originalIndex || "",
          10,
        );
        const sortIndex = isNaN(originalIndex) ? domIndex : originalIndex;

        if (mediaType === "image") {
          slides.push({
            slide: this.collectImageSlide(themeImg, productMedia, mediaId),
            originalIndex: sortIndex,
          });
        } else {
          slides.push({
            slide: this.collectVideoSlide(
              productMedia,
              mediaId,
              mediaType as "video",
            ),
            originalIndex: sortIndex,
          });
        }
      });

    slides.sort((a, b) => a.originalIndex - b.originalIndex);
    return slides.map((s) => s.slide);
  }

  private collectImageSlide(
    themeImg: HTMLElement,
    productMedia: HTMLElement,
    mediaId: string,
  ): MediaSlide {
    const fullSize = themeImg.dataset.fullSize || "";
    const img = productMedia.querySelector<HTMLImageElement>(
      ".rimage-wrapper > img",
    );
    const src =
      img?.currentSrc || img?.src || img?.dataset.src || fullSize || "";

    return {
      type: "image",
      mediaId,
      src: src.replace(/_{width}x\./, "_1080x."),
      fullSize: fullSize || src,
      alt: img?.alt || "",
      aspectRatio: parseFloat(img?.dataset.aspectratio || "1") || 1,
      videoHtml: "",
    };
  }

  private collectVideoSlide(
    productMedia: HTMLElement,
    mediaId: string,
    mediaType: "video",
  ): MediaSlide {
    const previewImg = productMedia.querySelector<HTMLImageElement>("img");
    const src = previewImg?.currentSrc || previewImg?.src || "";
    const aspectRatio =
      parseFloat(productMedia.dataset.aspectratio || "1.777") || 1.777; // 16 / 9

    const video = productMedia.querySelector<HTMLVideoElement>("video");
    let videoHtml = "";

    if (video) {
      const clone = video.cloneNode(true) as HTMLVideoElement;
      clone.setAttribute("autoplay", "");
      clone.setAttribute("playsinline", "");
      clone.muted = true;
      clone.controls = false;
      clone.loop = true;
      clone.classList.add("product-lightbox__video");
      videoHtml = clone.outerHTML;
    }

    return {
      type: mediaType,
      mediaId,
      src,
      fullSize: src,
      alt: "",
      aspectRatio,
      videoHtml,
    };
  }

  private getTemplate(selector: string): DocumentFragment | null {
    const template = this.querySelector<HTMLTemplateElement>(
      `template[${selector}]`,
    );
    return template?.content.cloneNode(true) as DocumentFragment | null;
  }

  private createOverlayElement(): HTMLElement | null {
    const fragment = this.getTemplate("data-lightbox-overlay");
    return (
      fragment?.querySelector<HTMLElement>(".product-lightbox__overlay") || null
    );
  }

  private createSlideElement(slide: MediaSlide): HTMLElement {
    if (slide.type === "image") {
      const fragment = this.getTemplate("data-lightbox-image-slide")!;
      const el = fragment.querySelector<HTMLElement>(".swiper-slide")!;
      const img = el.querySelector<HTMLImageElement>(
        ".product-lightbox__image",
      )!;
      img.src = slide.fullSize;
      img.alt = slide.alt;
      el.dataset.mediaId = slide.mediaId;
      return el;
    }

    const fragment = this.getTemplate("data-lightbox-video-slide")!;
    const el = fragment.querySelector<HTMLElement>(".swiper-slide")!;
    const wrapper = el.querySelector<HTMLElement>(
      ".product-lightbox__video-wrapper",
    )!;
    wrapper.style.aspectRatio = String(slide.aspectRatio);
    wrapper.innerHTML = slide.videoHtml;
    el.dataset.mediaId = slide.mediaId;
    return el;
  }

  private open(slides: MediaSlide[], startIndex: number) {
    this.overlay = this.createOverlayElement();
    if (!this.overlay) return;

    document.body.style.overflow = "hidden";
    const wrapper = this.overlay.querySelector<HTMLElement>(".swiper-wrapper")!;
    slides.forEach((slide) =>
      wrapper.appendChild(this.createSlideElement(slide)),
    );

    document.body.appendChild(this.overlay);

    this.overlay
      .querySelector(".product-lightbox__close")
      ?.addEventListener("click", () => this.close());

    this.overlay.addEventListener("click", (e) => {
      if (
        (e.target as HTMLElement).classList.contains(
          "product-lightbox__overlay",
        )
      ) {
        this.close();
      }
    });

    this.overlay
      .querySelectorAll<HTMLElement>(".product-lightbox__image-container")
      .forEach((container) => {
        container.addEventListener("click", (e) =>
          this.handleImageClick(e, container),
        );
      });

    this.boundKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") this.close();
      if (e.key === "ArrowRight") this.swiper?.slideNext();
      if (e.key === "ArrowLeft") this.swiper?.slidePrev();
    };
    document.addEventListener("keydown", this.boundKeyDown);

    // Trigger reflow then animate in
    this.overlay.offsetHeight;
    this.overlay.classList.add("product-lightbox__overlay--active");

    this.initSwiper(startIndex);
  }

  private close() {
    this.pauseAllVideos();
    this.resetZoom();

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.overlay) {
      this.overlay.classList.remove("product-lightbox__overlay--active");
      const el = this.overlay;
      setTimeout(() => el.remove(), CLOSE_TRANSITION_MS);
      this.overlay = null;
    }

    if (this.boundKeyDown) {
      document.removeEventListener("keydown", this.boundKeyDown);
      this.boundKeyDown = null;
    }

    document.body.style.overflow = "";
  }

  private initSwiper(startIndex: number) {
    if (!this.overlay) return;

    const swiperEl = this.overlay.querySelector<HTMLElement>(
      ".product-lightbox__swiper",
    );
    if (!swiperEl) return;

    const swiper = new Swiper(swiperEl, {
      modules: [Navigation],
      initialSlide: startIndex,
      navigation: {
        nextEl: ".product-lightbox__nav--next",
        prevEl: ".product-lightbox__nav--prev",
      },
      grabCursor: true,
      on: {
        slideChange: () => {
          this.resetZoom();
          this.pauseAllVideos();
          this.preloadAdjacentImages();
          this.updateCounter();
          this.autoPlayActiveVideo();
        },
        afterInit: (s: Swiper) => {
          this.swiper = s;
          this.preloadAdjacentImages();
          this.updateCounter();
          this.autoPlayActiveVideo();
        },
      },
    });
    this.swiper = swiper;
  }

  private preloadAdjacentImages() {
    if (!this.swiper || !this.overlay) return;

    const current = this.swiper.activeIndex;
    const slides = this.overlay.querySelectorAll<HTMLElement>(".swiper-slide");

    [current - 1, current + 1].forEach((idx) => {
      if (idx < 0 || idx >= slides.length) return;
      const img = slides[idx].querySelector<HTMLImageElement>(
        ".product-lightbox__image",
      );
      if (img && !img.complete && img.src) {
        const preload = new Image();
        preload.src = img.src;
      }
    });
  }

  private isSwiperDragging(themeImg: HTMLElement): boolean {
    const swiperContainer = themeImg.closest<HTMLElement>(".swiper-container");
    if (!swiperContainer) return false;
    const swiperId = swiperContainer.dataset.swiperId;
    return !!(
      swiperId &&
      (window as any).theme?.swipers?.[swiperId]?.allowClick === false
    );
  }

  private updateCounter() {
    if (!this.swiper || !this.overlay) return;
    const counter = this.overlay.querySelector<HTMLElement>(
      ".product-lightbox__counter",
    );
    if (counter) {
      const total = this.swiper.slides.length;
      const current = this.swiper.activeIndex + 1;
      counter.textContent = `${current} / ${total}`;
    }
  }

  private autoPlayActiveVideo() {
    if (!this.swiper || !this.overlay) return;
    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    if (!activeSlide) return;
    const video = activeSlide.querySelector<HTMLVideoElement>("video");
    if (video) {
      video.muted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Ignore autoplay errors (e.g., blocked by browser policies)
        });
      }
    }
  }

  private pauseAllVideos() {
    this.overlay
      ?.querySelectorAll<HTMLVideoElement>("video")
      .forEach((v) => v.pause());
  }
}

if (!customElements.get("product-lightbox")) {
  customElements.define("product-lightbox", ProductLightbox);
}

export default ProductLightbox;
